import React, {useCallback, useMemo, useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';
import {toast} from 'react-toastify';

import {validateCodeWithHsn} from 'api';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {
    PaginationControlsProps,
    SimpleTable,
    SimpleTablePagination,
} from 'components/pages/DocumentsCreate/InvoiceNew/Pages/InvoiceLines/components/SimpleTable';
import {Button, ButtonVariant} from 'components/ui-kit/Button';

import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {QRScannerModal} from 'components/ui-kit/ScanCode';
import {mapTableColumns} from 'components/ui-kit/Table2.0';

import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {
    createGdsFx,
    gdsCreatingStepChanged,
    goodsDeclarationDetails$,
    updateGdsCodeList,
} from 'models/documents/gds';
import {colors} from 'theme';
import {debounce} from 'utils/debounce';

import {goodsDeclarationValidation} from 'utils/markingCodes';

import {
    ControlItem,
    EmptyDataComponent,
    Form,
    FormControlWrapper,
    Headline,
    HeadlineH2,
    Root,
    StatusWrapper,
    SubText,
} from './styled';

export const GDSecondStep = ({allowToEdit}: {allowToEdit: boolean}) => {
    const [markingCode, setMarkingCode] = useState('');
    const [status, setStatus] = useState('-');
    const [scanShow, setScanShow] = useState(false);
    const [scanPending, setScanPending] = useState(false);
    const [paginationProps, setPaginationProps] = useState<PaginationControlsProps | null>(null);
    const {t} = useTranslation();
    const {gdsId, gdsCodeList, gdsStorageLocation, customsNameAndNumber} = useStore(
        goodsDeclarationDetails$,
    );

    const columnsSettings = [
        {
            id: 'markingCode',
            header: 'Marking Code',
        },
        {
            id: 'status',
            header: 'Status',
        },
        {
            id: 'control',
            header: 'Control',
            width: 48,
        },
    ];
    const columns = useMemo(() => mapTableColumns(columnsSettings), [columnsSettings]);

    const data = useMemo(
        () =>
            gdsCodeList.map((code, index) => ({
                markingCode: !gdsId ? (
                    code.code
                ) : (
                    <Link
                        to={{
                            pathname: generatePath(
                                ROUTE.DOCUMENTS_GDS_DECLARATION_TRANSPORT_CODES,
                                {
                                    id: gdsId,
                                    transportCode: code.code,
                                },
                            ),
                        }}
                    >
                        {code.code}
                    </Link>
                ),
                status: (
                    <StatusWrapper
                        color={PRODUCT_STATUS_COLORS[code.status as ProductStatus] ?? 'DEFAULT'}
                    >
                        {code.status
                            ? t(`dictionaries.products.status.${code.status as ProductStatus}`)
                            : '-'}
                    </StatusWrapper>
                ),
                control: allowToEdit && (
                    <IconButton
                        icon={<Icon source={CrossIcon} width={16} fillColor={colors.danger} />}
                        onClick={() => {
                            updateGdsCodeList({action: 'remove', index});
                        }}
                    />
                ),
            })),
        [gdsCodeList],
    );

    const onEnterCode = useCallback(
        async (code: string, fromLine?: boolean) => {
            if (fromLine && code.length < 10) {
                setMarkingCode('');
                setStatus('-');
                return;
            }
            try {
                setScanPending(true);
                const response = (
                    await validateCodeWithHsn(code, {
                        type: 'CUSTOMS_DECLARATION',
                        storageLocationId: gdsStorageLocation.value,
                    })
                ).data;
                const status = goodsDeclarationValidation(response, gdsCodeList);

                if (status.length) {
                    toast.error(
                        <div>
                            <strong>Errors</strong>
                            <ul style={{margin: '6px 0'}}>
                                {status.map((e) => (
                                    <li key={e}>{e}</li>
                                ))}
                            </ul>
                        </div>,
                    );
                    if (fromLine) {
                        setMarkingCode('');
                        setStatus('-');
                    }
                    return;
                }

                if (fromLine) {
                    setMarkingCode(response.info.identityCode);
                    setStatus(response.info.status);
                    return;
                }

                updateGdsCodeList({
                    data: {
                        code: response.info.identityCode,
                        status: response.info.status,
                    },
                });
            } catch (error) {
                toast.error((error as Error).message);
                setMarkingCode('');
                setStatus('-');
            } finally {
                setScanPending(false);
            }
        },
        [gdsCodeList],
    );

    const onScanCode = async (code: string) => {
        try {
            setScanPending(true);
            const response = (
                await validateCodeWithHsn(code, {
                    type: 'CUSTOMS_DECLARATION',
                    storageLocationId: gdsStorageLocation.value,
                })
            ).data;
            const status = goodsDeclarationValidation(response, gdsCodeList);

            if (status.length) {
                toast.error(
                    <div>
                        <strong>Errors</strong>
                        <ul style={{margin: '6px 0'}}>
                            {status.map((e) => (
                                <li key={e}>{e}</li>
                            ))}
                        </ul>
                    </div>,
                );
                return;
            }

            updateGdsCodeList({
                data: {
                    code: response.info.identityCode,
                    status: response.info.status,
                },
            });
        } catch (error) {
            console.error(error);
        } finally {
            setScanPending(false);
        }
    };

    const debouncedOnEnterCode = useMemo(
        () =>
            debounce((code: string) => {
                if (gdsCodeList.find((d) => d.code === code)) {
                    toast.error(t('components.scan.errors.double'));
                    setMarkingCode('');
                    setStatus('-');
                    return;
                }
                onEnterCode(code, true);
            }, 300),
        [onEnterCode],
    );

    const allowAdd = useMemo(() => markingCode.length > 0 && status !== '-', [markingCode, status]);

    const controlLineData = useMemo(
        () => [
            {
                markingCode: (
                    <Input
                        placeholder="Marking Code"
                        value={markingCode}
                        disabled={scanPending}
                        onChange={(e) => {
                            const trimmedVal = e.target.value.trim().replace(/\s/g, '');
                            setStatus('-');
                            setMarkingCode(trimmedVal);
                            debouncedOnEnterCode(trimmedVal);
                        }}
                    />
                ),
                status,
                control: (
                    <ControlItem>
                        <Button
                            size="sm"
                            variant={ButtonVariant.Normal}
                            type="button"
                            disabled={!allowAdd || scanPending}
                            onClick={() => {
                                updateGdsCodeList({
                                    data: {
                                        code: markingCode,
                                        status,
                                    },
                                });
                                setMarkingCode('');
                                setStatus('-');
                            }}
                        >
                            Add +
                        </Button>
                    </ControlItem>
                ),
            },
        ],
        [allowAdd, markingCode, status, gdsCodeList, scanPending],
    );

    const onSubmit = () => {
        createGdsFx({
            customsId: Number(customsNameAndNumber.value),
            storageLocation: gdsStorageLocation.value,
            markingCodeList: gdsCodeList.map((code) => code.code),
        });
    };

    return (
        <Root>
            <Form>
                <HeadlineH2>
                    {t('pages.documents.gds.secondStep.scanOrEnterAggregationCode')}
                </HeadlineH2>
                <FormControlWrapper>
                    {allowToEdit && (
                        <ControlItem>
                            <Button
                                variant={ButtonVariant.Normal}
                                type="button"
                                onClick={() => setScanShow(true)}
                            >
                                {t('components.scan.button')}
                                <Icon source={ScanIcon} width={20} height={20} />
                            </Button>
                        </ControlItem>
                    )}
                    <ControlItem style={{marginLeft: 'auto'}}>
                        <Button
                            variant={ButtonVariant.Secondary}
                            type="button"
                            onClick={() => {
                                gdsCreatingStepChanged(0);
                            }}
                        >
                            {t('pages.documents.gds.secondStep.previousStep')}
                        </Button>
                    </ControlItem>
                    {!gdsId && (
                        <ControlItem>
                            <Button
                                disabled={!data.length}
                                variant={ButtonVariant.Normal}
                                type="button"
                                onClick={onSubmit}
                            >
                                {t('pages.documents.gds.secondStep.createDocument')}
                            </Button>
                        </ControlItem>
                    )}
                </FormControlWrapper>
            </Form>
            {allowToEdit && (
                <SimpleTable
                    columns={columns}
                    data={controlLineData}
                    updateMyData={() => {}}
                    skipPageReset={false}
                    editableCell={false}
                    tableViewExtraSettings={{
                        tdThPadding: '8px 16px',
                    }}
                    cellContainerSettings="width: 100%; height: 60px;"
                />
            )}
            <SimpleTable
                hideHeaders={allowToEdit}
                columns={columns}
                data={data}
                updateMyData={() => {}}
                skipPageReset={false}
                editableCell={false}
                onPaginationReady={setPaginationProps}
                tableViewExtraSettings={{
                    tdThPadding: '8px 16px',
                }}
                initialState={{pageIndex: 0, pageSize: 6}}
                emptyDataComponent={
                    <EmptyDataComponent>
                        <Headline>{t('pages.documents.gds.secondStep.scannedCodes')}</Headline>
                        <SubText>{t('pages.documents.gds.secondStep.enterCodeManually')}</SubText>
                    </EmptyDataComponent>
                }
            />
            {paginationProps && data.length > 0 && (
                <div>
                    <SimpleTablePagination {...paginationProps} />
                </div>
            )}
            <QRScannerModal
                open={scanShow}
                pending={scanPending}
                onClose={() => setScanShow(false)}
                onScan={onScanCode}
                onManual={onEnterCode}
            />
        </Root>
    );
};
